export type SessionFlowSnapshotMutationKind =
  | 'patch'
  | 'preview_guided_fix'
  | 'assign_sheets'
  | 'complete_preview_step'
  | 'refresh_preview'
  | 'shuffle_preview'
  | 'create_audience'
  | 'retry_create'
  | 'retry_analysis'
  | 'discard'
  | 'enter_context_materials'
  | 'complete_skip_context_materials'
  | 'upload_context_material'
  | 'remove_context_material'

export type IdentityFlowSnapshotMutationKind =
  | 'patch_field'
  | 'complete_required_fields'
  | 'refresh_preview'
  | 'create_audience'
  | 'retry_create'
  | 'discard'

export type FlowSnapshotRouteImpact = 'non_routing' | 'route_affecting'
export type FlowSnapshotUpdateStrategy =
  | 'server_revalidate'
  | 'clear'

type FlowSnapshotPolicyEntry = {
  routeImpact: FlowSnapshotRouteImpact
  updateStrategy: FlowSnapshotUpdateStrategy
  rationale: string
}

// FI-02 checked-in audit list: which actions are truly non-routing, and which still
// require a route fetch because local caches are not authoritative enough yet.
export const SESSION_FLOW_SNAPSHOT_REVALIDATION_POLICY: Record<
  SessionFlowSnapshotMutationKind,
  FlowSnapshotPolicyEntry
> = {
  patch: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Audience-draft edits must revalidate the canonical server snapshot instead of rebuilding it locally.',
  },
  preview_guided_fix: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Preview fixes can affect routing and must refresh the canonical server snapshot.',
  },
  assign_sheets: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Workbook assignment changes branch state and route eligibility, so the server snapshot stays authoritative.',
  },
  complete_preview_step: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Preview-to-required-fields handoff stays on explicit server refresh until thin acks land.',
  },
  refresh_preview: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Refresh-preview moves preview state through queued/running server phases and must revalidate the canonical snapshot.',
  },
  shuffle_preview: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Even non-routing preview updates should use the server snapshot instead of client reconstruction.',
  },
  create_audience: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Create uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  retry_create: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Retry-create uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  retry_analysis: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Retry-analysis restarts server-owned workflow state and must refresh the canonical snapshot.',
  },
  discard: {
    routeImpact: 'route_affecting',
    updateStrategy: 'clear',
    rationale: 'Discard removes the in-flow route entirely, so the cached snapshot should be cleared.',
  },
  enter_context_materials: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Context-material branch state remains server-owned even when the route stays the same.',
  },
  complete_skip_context_materials: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Complete/skip uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  upload_context_material: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Context-material file changes update server-owned branch counters and should not synthesize snapshot state locally.',
  },
  remove_context_material: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Context-material file changes update server-owned branch counters and should not synthesize snapshot state locally.',
  },
}

export const IDENTITY_FLOW_SNAPSHOT_REVALIDATION_POLICY: Record<
  IdentityFlowSnapshotMutationKind,
  FlowSnapshotPolicyEntry
> = {
  patch_field: {
    routeImpact: 'non_routing',
    updateStrategy: 'server_revalidate',
    rationale: 'Identity edits must revalidate the canonical server snapshot instead of merging shell data locally.',
  },
  complete_required_fields: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Required-fields completion uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  refresh_preview: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Refresh-preview transitions are server-owned and must refresh the canonical snapshot.',
  },
  create_audience: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Create uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  retry_create: {
    routeImpact: 'route_affecting',
    updateStrategy: 'server_revalidate',
    rationale: 'Retry-create uses an explicit route acknowledgement in the hook; the fallback policy should still prefer server truth.',
  },
  discard: {
    routeImpact: 'route_affecting',
    updateStrategy: 'clear',
    rationale: 'Discard removes the in-flow route entirely, so the cached snapshot should be cleared.',
  },
}
